import type { SxProps, Theme } from '@mui/material';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { getAnswerCardSx } from 'views/template/builder/components/checklistItemNode/answerConfig/answerTypeTheme';
import { Color } from 'shared/utils/colors';

/** Colored answer card (template preview) + field layout for edit/preview forms. */
export const getAnswerFieldCardSx = (answerType: AnswerType): SxProps<Theme> => ({
  ...getAnswerCardSx(answerType),
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
});

/** Muted card when the answer type is marked as not applicable. */
export const getAnswerFieldCardSkippedSx = (
  answerType: AnswerType
): SxProps<Theme> => ({
  ...getAnswerFieldCardSx(answerType),
  bgcolor: Color.solidGray,
});

/** Inner field layout only — used when the parent already draws the answer card. */
export const answerFieldBodySx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  width: '100%',
  minWidth: 0,
};
export const answerLabelSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  fontWeight: 700,
  color: 'text.primary',
  lineHeight: 1.5,
  m: 0,
  overflowWrap: 'anywhere',
};

export const requiredIndicatorSx: SxProps<Theme> = {
  color: 'inherit',
  ml: 0.5,
};

export const answerContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};

export const inputContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};

/** Width shell for numeric fields — flex basis on the wrapper, not the TextField. */
export const numericInputMaxThirdSx: SxProps<Theme> = {
  width: '33.333%',
  maxWidth: '33.333%',
  flex: '0 0 33.333%',
  minWidth: 96,
  boxSizing: 'border-box',
};

export const numericRowWithSliderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 2,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};

export const numericInputColumnSx: SxProps<Theme> = numericInputMaxThirdSx;

export const numericUnitColumnSx: SxProps<Theme> = {
  flexShrink: 0,
};

export const numericSliderSx: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
};

export const unitLabelSx: SxProps<Theme> = {
  fontSize: '0.875rem',
  color: Color.textPrimary,
  flexShrink: 0,
};

export const warningTextSx: SxProps<Theme> = {
  color: Color.warningOrange,
  fontSize: '0.75rem',
  marginTop: 0.5,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
};

/** Theme removes disabled outlined borders globally; keep a visible frame in inspections. */
export const disabledOutlinedInputBorderSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    border: '1px solid',
    borderColor: Color.disabledGray,
  },
};

/** Soft warning border for numeric answers outside template norm (not MUI error). */
export const numericInputNormWarningSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: Color.warningOrange,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: Color.warningOrange,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: Color.warningOrange,
    },
  },
};

export const sliderContainerSx: SxProps<Theme> = {
  maxWidth: '33.333%',
  flex: '1 1 33.333%',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  pr: 1.5,
};

/** Narrowest option column; below two of these the list falls back to one. */
const OPTION_COLUMN_MIN_WIDTH_PX = 280;

/**
 * Checkbox / radio options as in the other forms (template wizard): default
 * MUI size and label typography, with the control's glyph aligned to the field
 * label via the default `FormControlLabel` inset. Two columns when they fit.
 */
export const optionListSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(min(${OPTION_COLUMN_MIN_WIDTH_PX}px, 100%), 1fr))`,
  columnGap: 2,
  rowGap: 0,
  width: '100%',
  '& .MuiFormControlLabel-root': {
    mr: 0,
    minWidth: 0,
    alignItems: 'center',
  },
  '& .MuiFormControlLabel-label': {
    color: Color.textPrimary,
    overflowWrap: 'anywhere',
  },
};

export const answersStackSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
};
